import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

/**
 * A custom element that allows the user to clean a search input.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchPageInput - The search input element.
 * @extends {Component<Refs>}
 */
/**
 * Twin of r69EngineQuery in predictive-search.js — keep identical. The results page's own input
 * must send the same query form as the header panel, or re-searching from the page would find a
 * different set than the panel promised. No `*`: it makes the engine return the whole catalog.
 * @param {string} raw
 * @returns {string}
 */
function r69EngineQuery(raw) {
  return raw.replace(/\*/g, ' ').trim().replace(/\s+/g, ' ');
}

class SearchPageInputComponent extends Component {
  requiredRefs = ['searchPageInput'];

  connectedCallback() {
    super.connectedCallback();
    // The <form> wraps this component, so its submit is bound here rather than via on:submit.
    this.refs.searchPageInput.form?.addEventListener('submit', this.#onSubmit);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.searchPageInput.form?.removeEventListener('submit', this.#onSubmit);
  }

  /** @param {Event} event */
  #onSubmit = (event) => {
    const input = this.refs.searchPageInput;
    const engine = r69EngineQuery(input.value);
    if (engine === '') return; // the empty search is the reset, submitted as is
    event.preventDefault();
    const url = new URL(input.form?.action || Theme.routes.search_url, location.origin);
    url.search = '';
    url.searchParams.set('q', engine);
    url.searchParams.set('type', 'product');
    window.location.href = url.toString();
  };

  /**
   * Handles the keydown event on the search input and resets the search when
   * empty and Escape is pressed.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown = debounce((event) => {
    const value = this.refs.searchPageInput.value.trim();

    if (event.key === 'Escape' && value === '') {
      this.#submitEmptySearch();
    }
  }, 100);

  #submitEmptySearch() {
    const searchInput = this.refs.searchPageInput;

    searchInput.focus();
    searchInput.value = '';

    if (this.#isEmptyState()) return;

    searchInput.form?.submit();
  }

  #isEmptyState = () => {
    const url = new URL(window.location.href);
    const queryParam = url.searchParams.get('q') ?? '';

    return queryParam.trim() === '';
  };
}

if (!customElements.get('search-page-input-component')) {
  customElements.define('search-page-input-component', SearchPageInputComponent);
}
